import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import * as api from "../api/api";
import "./AdminEdit.css";
import LoadingSpinner from "./LoadingSpinner";

const AdminLocationEdit = () => {
  const navigate = useNavigate();
  const { locationSlug } = useParams();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [isLastEnabled, setIsLastEnabled] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [checkingToggle, setCheckingToggle] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const res = await api.getLocation(locationSlug);
        if (mounted) setLocation(res);
      } catch (err) {
        console.error("Failed to load location", err);
        // fallback: try loading locations list and find
        try {
          const list = await api.getLocations();
          const found = list.find((l) => l.slug === locationSlug);
          if (mounted) setLocation(found || null);
        } catch (err2) {
          console.error("Failed to fallback load locations", err2);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [locationSlug]);

  // Re-check whether this is the last enabled clinic
  useEffect(() => {
    let mounted = true;
    const check = async () => {
      if (!location) return;
      try {
        const list = await api.getLocations();
        if (!mounted) return;
        const enabled = list.filter((l) => l.enabled !== false);
        const last = enabled.length === 1 && enabled[0].slug === locationSlug;
        setIsLastEnabled(last);
      } catch (err) {
        console.warn("Failed to check enabled clinics", err);
      }
    };
    check();
    return () => {
      mounted = false;
    };
  }, [location, locationSlug]);

  const updateField = (field, value) =>
    setLocation((prev) => ({ ...prev, [field]: value }));

  const toggleEnabled = () =>
    setLocation((prev) => ({ ...prev, enabled: !prev.enabled }));

  const handleToggleClick = async (e) => {
    e.stopPropagation();
    if (!location) return;
    const currentlyEnabled = !!location.enabled;
    // if attempting to disable, validate server-side that at least one will remain
    setCheckingToggle(true);
    try {
      if (currentlyEnabled) {
        const list = await api.getLocations();
        const enabled = list
          .filter((l) => l.enabled !== false)
          .map((l) => l.slug);
        // if only this clinic is enabled on the server, block
        if (enabled.length === 1 && enabled[0] === locationSlug) {
          setShowBanner(true);
          setTimeout(() => setShowBanner(false), 3000);
          return;
        }
      }

      // proceed: optimistic update and persist immediately
      const newValue = !currentlyEnabled;
      setLocation((prev) => ({ ...prev, enabled: newValue }));
      try {
        const payload = { ...location, enabled: newValue };
        // ensure we don't send stale createdAt
        delete payload.createdAt;
        const saved = await api.updateLocation(locationSlug, payload);
        if (saved) setLocation(saved);
      } catch (err) {
        // revert optimistic change on failure
        setLocation((prev) => ({ ...prev, enabled: currentlyEnabled }));
        console.error("Failed to save enabled state", err);
        setShowBanner(true);
        setTimeout(() => setShowBanner(false), 3000);
      }
    } catch (err) {
      console.warn("Failed during toggle validation", err);
      // fallback: optimistic toggle
      setLocation((prev) => ({ ...prev, enabled: !prev.enabled }));
    } finally {
      setCheckingToggle(false);
    }
  };

  const addHour = () =>
    setLocation((prev) => ({
      ...prev,
      hours: [...(prev.hours || []), { day: "Lundi", time: "09:00 - 18:00" }],
    }));
  const removeHour = (i) =>
    setLocation((prev) => ({
      ...prev,
      hours: prev.hours.filter((_, idx) => idx !== i),
    }));
  const updateHour = (i, field, value) => {
    const next = [...(location.hours || [])];
    next[i] = { ...next[i], [field]: value };
    setLocation((prev) => ({ ...prev, hours: next }));
  };

  const handleSave = async () => {
    try {
      await api.updateLocation(locationSlug, location);
      // saved on server: clear any pending-disabled marker for this slug
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error("Failed to save location", err);
      alert("Erreur lors de la sauvegarde. Voir la console.");
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!location) return <div>Lieu introuvable</div>;

  return (
    <div className="admin-edit-page">
      <div className="admin-header">
        <div className="container">
          <button
            className="back-btn"
            onClick={() =>
              navigate("/admin/dashboard", {
                state: { selectedLocation: locationSlug },
              })
            }
          >
            <ArrowLeft size={20} /> Retour au dashboard
          </button>
          <div className="admin-header-content">
            <h1 className="heading-md">Modifier - Cabinet</h1>
            <button
              className="btn-primary"
              onClick={handleSave}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Save size={18} /> {saved ? "✓ Sauvegardé" : "Sauvegarder"}
            </button>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="edit-section">
            <h2 className="heading-sm">Informations générales</h2>
            <div className="form-group">
              <label className="form-label">Activer le cabinet</label>
              <div
                onClick={handleToggleClick}
                style={{ display: "inline-block" }}
              >
                <label
                  className="switch"
                  style={{
                    cursor:
                      checkingToggle || (isLastEnabled && location?.enabled)
                        ? "not-allowed"
                        : "pointer",
                    opacity: checkingToggle ? 0.6 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!location.enabled}
                    readOnly
                    disabled={
                      checkingToggle || (isLastEnabled && location?.enabled)
                    }
                  />
                  <span className="slider" />
                </label>
              </div>
              {showBanner && (
                <div
                  className="ephemeral-banner"
                  style={{
                    marginTop: "8px",
                    color: "#065f46",
                    background: "#ecfdf5",
                    padding: "8px",
                    borderRadius: "6px",
                  }}
                >
                  Au moins un cabinet doit rester activé.
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Afficher la carte de direction</label>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={!!location.showDirectionCard}
                  onChange={(e) => updateField("showDirectionCard", e.target.checked)}
                />
                <span className="slider" />
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Nom</label>
              <input
                className="form-input"
                value={location.name || ""}
                onChange={(e) => updateField("name", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Adresse</label>
              <input
                className="form-input"
                value={location.address || ""}
                onChange={(e) => updateField("address", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Téléphone</label>
              <input
                className="form-input"
                value={location.phone || ""}
                onChange={(e) => updateField("phone", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-input"
                value={location.email || ""}
                onChange={(e) => updateField("email", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Lien Doctolib</label>
              <input
                type="url"
                className="form-input"
                value={location.doctolib || ""}
                onChange={(e) => updateField("doctolib", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Map Embed URL</label>
              <input
                className="form-input"
                value={location.mapEmbed || ""}
                onChange={(e) => updateField("mapEmbed", e.target.value)}
              />
            </div>

            <div className="edit-section">
              <div className="section-header">
                <h2 className="heading-sm">Horaires</h2>
                <button className="btn-secondary btn-small" onClick={addHour}>
                  <Plus size={14} /> Ajouter
                </button>
              </div>

              {(location.hours || []).map((h, idx) => (
                <div key={idx} className="list-item-edit">
                  <div className="form-group">
                    <label className="form-label">Jour</label>
                    <input
                      className="form-input"
                      value={h.day}
                      onChange={(e) => updateHour(idx, "day", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Heures</label>
                    <input
                      className="form-input"
                      value={h.time}
                      onChange={(e) => updateHour(idx, "time", e.target.value)}
                    />
                  </div>
                  <button
                    className="btn-delete"
                    onClick={() => removeHour(idx)}
                  >
                    <Trash2 size={14} /> Supprimer
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bottom-actions">
            <button className="btn-primary btn-large" onClick={handleSave}>
              <Save size={18} /> Enregistrer toutes les modifications
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminLocationEdit;
